import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiFetch } from "./sdk"

/**
 * Admin → Messages (backend lib/cs-inbox.ts): contact-form messages from the storefront, the
 * customer they match, and the CS team's status / category / assignee / notes.
 */

export type MessageStatus = "new" | "open" | "waiting_on_customer" | "closed"
export type MessageCategory = "cancel" | "pause_skip" | "billing_card" | "discount" | "shipping_order" | "other"
export type InboxMessage = {
  id: string
  source: string
  name: string
  email: string
  phone: string | null
  message: string
  status: MessageStatus
  category: MessageCategory
  category_set_by: string | null
  assigned_to: string | null
  customer_id: string | null
  customer_linked_by: "email" | "staff" | null
  notes: string | null
  excerpt_only: boolean
  replied_at: string | null
  replied_by: string | null
  closed_at: string | null
  created_at: string
  updated_at: string
  customer: { id: string; email: string; first_name: string | null; last_name: string | null; has_account: boolean } | null
  /** Replies sent from the message page, oldest first (only on the single-message answer). */
  replies?: MessageReply[]
}
/** A reply sent from admin → Messages (backend lib/cs-inbox.ts sendReply). */
export type MessageReply = {
  id: string
  message_id: string
  sent_by: string
  sent_by_name: string | null
  sent_by_email: string | null
  to_email: string
  reply_to: string | null
  subject: string
  body: string
  /** sent: handed to the email service; logged: email isn't set up on this server; failed: not sent. */
  status: "sent" | "logged" | "failed"
  transport: string | null
  error: string | null
  created_at: string
}
export type ReplyDefaults = { subject: string; reply_to: string }
export type InboxMessageResponse = { message: InboxMessage; reply_defaults?: ReplyDefaults }
export type InboxCounts = Record<MessageStatus | "active" | "all", number>
export type InboxList = { messages: InboxMessage[]; count: number; counts: InboxCounts }
export type Assignee = { user_id: string; email: string; name: string | null }
export type MessageChange = {
  status?: MessageStatus
  category?: MessageCategory
  assigned_to?: string | null
  notes?: string | null
  customer_id?: string | null
  replied?: true
}

export const STATUS_LABEL: Record<MessageStatus, string> = { new: "New", open: "Open", waiting_on_customer: "Waiting on customer", closed: "Closed" }
export const STATUS_TONE: Record<MessageStatus, "info" | "attention" | "neutral" | "success"> = { new: "info", open: "attention", waiting_on_customer: "neutral", closed: "success" }
export const CATEGORY_LABEL: Record<MessageCategory, string> = {
  cancel: "Cancel",
  pause_skip: "Pause or skip",
  billing_card: "Billing or card",
  discount: "Discount",
  shipping_order: "Shipping or order",
  other: "Other",
}

export function useInbox(qs: string) {
  return useQuery({ queryKey: ["inbox", "list", qs], queryFn: () => apiFetch<InboxList>(`/admin/terify/inbox?${qs}`), placeholderData: keepPreviousData, refetchInterval: 45_000 })
}

/** New messages: the number on the Messages nav item. */
export function useInboxCounts(enabled: boolean) {
  return useQuery({
    queryKey: ["inbox", "counts"],
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: () => apiFetch<{ new: number; mine: number }>("/admin/terify/inbox/counts"),
  })
}

export function useInboxMessage(id: string) {
  return useQuery({ queryKey: ["inbox", "message", id], queryFn: () => apiFetch<InboxMessageResponse>(`/admin/terify/inbox/m/${encodeURIComponent(id)}`) })
}

export function useAssignees(enabled = true) {
  return useQuery({ queryKey: ["inbox", "assignees"], enabled, staleTime: 5 * 60_000, queryFn: () => apiFetch<{ assignees: Assignee[] }>("/admin/terify/inbox/assignees") })
}

export function useUpdateMessage(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: MessageChange) => apiFetch<{ message: InboxMessage }>(`/admin/terify/inbox/m/${encodeURIComponent(id)}`, { method: "POST", body: JSON.stringify(b) }),
    // Keeps reply_defaults (only the GET has them).
    onSuccess: (r) => {
      qc.setQueryData<InboxMessageResponse>(["inbox", "message", id], (old) => ({ ...old, ...r }))
      qc.invalidateQueries({ queryKey: ["inbox", "list"] })
      qc.invalidateQueries({ queryKey: ["inbox", "counts"] })
    },
  })
}

/** "Send" in the reply box: emails the customer from the store and marks the message replied. */
export function useSendReply(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: { subject: string; body: string }) =>
      apiFetch<{ message: InboxMessage; reply: MessageReply }>(`/admin/terify/inbox/m/${encodeURIComponent(id)}/reply`, { method: "POST", body: JSON.stringify(b) }),
    onSuccess: (r) => {
      qc.setQueryData<InboxMessageResponse>(["inbox", "message", id], (old) => ({ ...old, message: r.message }))
      qc.invalidateQueries({ queryKey: ["inbox", "list"] })
      qc.invalidateQueries({ queryKey: ["inbox", "counts"] })
    },
    // A refused send is kept under the message as "Not sent": show it.
    onError: () => qc.invalidateQueries({ queryKey: ["inbox", "message", id] }),
  })
}

/** The first name the reply starts with: the first word of the name they gave on the form, else the linked customer's. */
export function replyFirstName(m: Pick<InboxMessage, "name" | "customer">) {
  return (m.name.trim().split(/\s+/)[0] || m.customer?.first_name?.trim() || "").trim()
}

/** What the reply box starts with: "Hi <first name>,", a blank line, then the staff member writes. */
export const replyGreeting = (m: Pick<InboxMessage, "name" | "customer">) => `Hi ${replyFirstName(m) || "there"},\n\n`

export const DEFAULT_REPLY_SUBJECT = "Re: your message to Tolvane Labs"

/**
 * "Open in Gmail" (fallback when sending from here isn't possible): Gmail's compose window in a new
 * tab, to the customer, with the subject and the text from the reply box and their message quoted.
 * The reply then goes from the staff member's own Gmail, so it isn't kept here: "Mark replied" after.
 */
export function gmailComposeUrl(m: Pick<InboxMessage, "email" | "name" | "message" | "created_at">, subject: string, draft: string) {
  const when = new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
  const quoted = m.message
    .split(/\r?\n/)
    .map((l) => `> ${l}`)
    .join("\n")
  const body = `${draft.replace(/\s+$/, "")}\n\n\nOn ${when}, ${m.name} wrote:\n${quoted}`
  // Long URLs get cut by browsers and proxies: keep the compose link well under 8 KB.
  const p = new URLSearchParams({ view: "cm", fs: "1", to: m.email, su: subject, body: body.slice(0, 3000) })
  return `https://mail.google.com/mail/?${p.toString().replace(/\+/g, "%20")}`
}
